import React from 'react'

const isEditing = () => {
  const el = document.activeElement
  if (!el) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable
}

/**
 * The pixel height a mobile WebView table page should pin itself to, or 0 while
 * no trustworthy height is known yet.
 *
 * The page is pinned rather than left at 100% so the keyboard can't collapse
 * the table (NK-201): on Android, tapping an editable cell opens the keyboard,
 * which shrinks window.innerHeight; a 100%-height scroll chain then collapses,
 * @tanstack/react-virtual re-measures and the focused input remounts — the
 * keyboard flashes up and dismisses. So while a field has focus the height
 * only ever grows.
 *
 * With no field focused it follows innerHeight both ways. Holding the largest
 * value ever seen instead broke Android: the WebView can report a taller
 * innerHeight for a moment while the page loads (seen: 1162 against a real 739),
 * and the page then stayed pinned taller than the WebView — the rows below the
 * visible bottom could never be scrolled into view.
 *
 * 0 means "render unconstrained": iOS WKWebView computes `vh` as 0 for a
 * webview mounted with no size and does not reliably recompute it, and this
 * route can mount offscreen (ADR-009) reporting innerHeight 0, so callers must
 * not fall back to `100vh`.
 */
const useWebviewPageHeight = () => {
  const [pageHeight, setPageHeight] = React.useState(() => window.innerHeight)

  React.useEffect(() => {
    const update = () => {
      const current = window.innerHeight
      setPageHeight((h) => (isEditing() ? Math.max(h, current) : current))
    }
    // Re-run once now in case the real layout landed between render and effect.
    update()
    window.addEventListener('resize', update)
    // iOS WKWebView drives visualViewport rather than firing window `resize` for
    // keyboard/layout changes; window.innerHeight stays the stable layout height
    // there, so reading it from a visualViewport event is still correct.
    const vv = window.visualViewport
    vv?.addEventListener('resize', update)
    // A rotation legitimately changes the baseline, even mid-edit.
    const reset = () => setPageHeight(window.innerHeight)
    window.addEventListener('orientationchange', reset)
    return () => {
      window.removeEventListener('resize', update)
      vv?.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', reset)
    }
  }, [])

  return pageHeight
}

export default useWebviewPageHeight
